import { useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Trash, Eye } from "@phosphor-icons/react";
import { BtnPrimary, BtnSecondary, BtnDestructive, ProgressBanner, Pagination } from "@/pages/Senders";

const STANDARD = ["email","firstName","lastName","fullName","phone","loanAmount","address","city","state","zip","ctaLink","companyName"];
const RECIPIENTS_BATCH_SIZE = 100;

export default function Recipients() {
  const [lists, setLists] = useState([]);
  const [preview, setPreview] = useState(null);  // { headers, sample_rows, file, name, mapping, duplicate_strategy }
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { total, done, valid, invalid, dup, cancelled }
  const uploadCancelRef = useRef(false);
  const fileRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const load = async () => {
    const r = await api.get("/recipients/lists", { params: { limit: pageSize, skip: page * pageSize } });
    setLists(r.data.lists);
    setTotalCount(r.data.total || 0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  const startUpload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/recipients/preview", fd);
      const mapping = {};
      for (const h of r.data.headers) {
        if (STANDARD.includes(h)) mapping[h] = h;
        else mapping[h] = h; // default: use as custom field
      }
      setPreview({ ...r.data, file, name: file.name.replace(/\.csv$/i, ""), mapping, duplicate_strategy: "skip" });
    } catch {
      toast.error("Preview failed");
    }
  };

  const parseCsvFile = (file) =>
    new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (h) => (h || "").trim(),
        complete: (res) => resolve({
          rows: (res.data || []).map((r, i) => ({ ...r, _row: i + 2 })),
          error: null,
        }),
        error: (err) => resolve({ rows: [], error: err.message }),
      });
    });

  const doImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      // 1. Parse the CSV on the client.
      const parsed = await parseCsvFile(preview.file);
      if (parsed.error || parsed.rows.length === 0) {
        toast.error("Could not parse CSV");
        setBusy(false);
        return;
      }
      // 2. Create the empty list on the server.
      const initRes = await api.post("/recipients/lists/init", {
        name: preview.name,
        mapping: preview.mapping,
        duplicate_strategy: preview.duplicate_strategy,
      });
      const listId = initRes.data.list.id;

      // 3. Close the mapping modal and open the progress banner.
      setPreview(null);
      uploadCancelRef.current = false;
      setUploadProgress({
        total: parsed.rows.length, done: 0,
        valid: 0, invalid: 0, dup: 0, cancelled: false,
      });

      // 4. Stream rows in RECIPIENTS_BATCH_SIZE chunks.
      for (let start = 0; start < parsed.rows.length; start += RECIPIENTS_BATCH_SIZE) {
        if (uploadCancelRef.current) {
          setUploadProgress((p) => ({ ...p, cancelled: true }));
          break;
        }
        const chunk = parsed.rows.slice(start, start + RECIPIENTS_BATCH_SIZE);
        try {
          const r = await api.post(`/recipients/lists/${listId}/append-batch`, { rows: chunk });
          const d = r.data || {};
          setUploadProgress((p) => ({
            ...p,
            done: p.done + chunk.length,
            valid: p.valid + (d.valid || 0),
            invalid: p.invalid + (d.invalid || 0),
            dup: p.dup + (d.duplicates || 0),
          }));
        } catch (e) {
          setUploadProgress((p) => ({
            ...p,
            done: p.done + chunk.length,
            invalid: p.invalid + chunk.length,
          }));
        }
      }

      // 5. Finalize (applies keep_last dedup if selected).
      try { await api.post(`/recipients/lists/${listId}/finalize`); } catch { /* ignore */ }

      setUploadProgress((p) => {
        const label = uploadCancelRef.current ? "Import cancelled" : "Import complete";
        toast.success(`${label} · ${p.valid} valid · ${p.invalid} invalid · ${p.dup} duplicates`);
        return p;
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      setTimeout(() => setUploadProgress(null), 4000);
    }
  };

  const cancelUpload = () => { uploadCancelRef.current = true; };

  const delList = async (id) => {
    if (!confirm("Delete this list?")) return;
    await api.delete(`/recipients/lists/${id}`);
    toast.success("List deleted");
    load();
  };

  const view = async (id) => {
    const r = await api.get(`/recipients/lists/${id}`);
    setDetail(r.data);
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Data</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Recipient lists</h1>
        </div>
        <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }}
          data-testid="recipients-file-input"
          onChange={(e) => e.target.files?.[0] && startUpload(e.target.files[0])} />
        <BtnPrimary onClick={() => fileRef.current?.click()} testid="recipients-upload-btn">
          <UploadSimple size={14} weight="bold" /> Upload CSV
        </BtnPrimary>
      </div>

      {uploadProgress && (
        <ProgressBanner
          testid="recipients-progress"
          title="Importing recipients"
          progress={uploadProgress}
          onCancel={cancelUpload}
          stats={[
            { label: "Valid", value: uploadProgress.valid, tone: "success" },
            { label: "Duplicates", value: uploadProgress.dup, tone: "warn" },
            { label: "Invalid", value: uploadProgress.invalid, tone: "error" },
          ]}
        />
      )}

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="recipient-lists-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="num">Total</th>
              <th className="num">Valid</th>
              <th className="num">Invalid</th>
              <th className="num">Duplicates</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lists.length === 0 && (
              <tr><td colSpan={7} className="text-center text-[#71717A] py-8 font-mono">[ no recipient lists ]</td></tr>
            )}
            {lists.map((l) => (
              <tr key={l.id}>
                <td className="font-medium">{l.name}</td>
                <td className="num data-num">{l.total_rows}</td>
                <td className="num data-num text-[#00FF66]">{l.valid_rows}</td>
                <td className="num data-num text-[#FF3B30]">{l.invalid_rows}</td>
                <td className="num data-num text-[#FF9500]">{l.duplicate_rows}</td>
                <td className="text-[#71717A] text-xs font-mono">{l.created_at?.slice(0, 19)}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => view(l.id)} data-testid={`view-list-${l.id}`}
                      className="w-7 h-7 flex items-center justify-center border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA]">
                      <Eye size={12} weight="bold" />
                    </button>
                    <button onClick={() => delList(l.id)} data-testid={`delete-list-${l.id}`}
                      className="w-7 h-7 flex items-center justify-center border border-[#2A2A2A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#A1A1AA]">
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
          testidPrefix="recipients-pagination"
        />
      )}


      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="import-modal">
          <div className="w-full max-w-4xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-[#2A2A2A]">
              <div className="micro mb-1">Step 2 / 4</div>
              <h3 className="font-heading text-xl font-semibold">Map columns · {preview.file.name}</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="micro block mb-1.5">List name</span>
                  <input value={preview.name} onChange={(e) => setPreview({ ...preview, name: e.target.value })}
                    data-testid="import-name"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
                </label>
                <label className="block">
                  <span className="micro block mb-1.5">Duplicate strategy</span>
                  <select value={preview.duplicate_strategy}
                    onChange={(e) => setPreview({ ...preview, duplicate_strategy: e.target.value })}
                    data-testid="import-dup-strategy"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
                    <option value="skip">Skip duplicates</option>
                    <option value="keep_first">Keep first</option>
                    <option value="keep_last">Keep last</option>
                    <option value="import_duplicates">Import all</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {preview.headers.map((h) => (
                  <label key={h} className="flex items-center gap-3">
                    <span className="font-mono text-xs text-[#A1A1AA] w-40 truncate">{h}</span>
                    <span className="text-[#71717A]">→</span>
                    <select
                      data-testid={`map-${h}`}
                      value={preview.mapping[h] || ""}
                      onChange={(e) => setPreview({ ...preview, mapping: { ...preview.mapping, [h]: e.target.value } })}
                      className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
                      <option value="ignore">Ignore</option>
                      {STANDARD.map((s) => <option key={s} value={s}>{s}</option>)}
                      <option value={h}>Custom: {h}</option>
                    </select>
                  </label>
                ))}
              </div>
              <div>
                <div className="micro mb-2">Sample rows</div>
                <div className="border border-[#2A2A2A] overflow-x-auto max-h-56">
                  <table className="dtable w-full text-xs">
                    <thead>
                      <tr>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.sample_rows.slice(0, 10).map((r, i) => (
                        <tr key={i}>{preview.headers.map((h) => <td key={h} className="font-mono">{r[h]}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[#2A2A2A] flex gap-2 justify-end">
              <BtnSecondary onClick={() => setPreview(null)} testid="import-cancel">Cancel</BtnSecondary>
              <BtnPrimary onClick={doImport} testid="import-submit" disabled={busy}>
                {busy ? "IMPORTING..." : "Import list"}
              </BtnPrimary>
            </div>
          </div>
        </div>
      )}

      {detail && (() => {
        // Build the full column list dynamically so every uploaded field
        // shows up in preview — standard fields first, then any keys from
        // custom_fields dicts, all deduplicated and ordered consistently.
        const STANDARD = [
          "email", "first_name", "last_name", "full_name", "company_name",
          "phone", "loan_amount", "address", "city", "state", "zip", "cta_link",
        ];
        const customKeys = new Set();
        for (const r of detail.recipients_sample || []) {
          for (const k of Object.keys(r.custom_fields || {})) customKeys.add(k);
        }
        const columns = [...STANDARD, ...[...customKeys].sort()];
        const rowValue = (r, col) => {
          if (col in r && r[col] != null && r[col] !== "") return r[col];
          return (r.custom_fields || {})[col] ?? "";
        };
        return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-[#2A2A2A] flex justify-between items-center sticky top-0 bg-[#0A0A0A] z-10">
              <div>
                <div className="micro mb-1">Preview · showing {detail.recipients_sample?.length || 0} of {detail.list.total_rows}</div>
                <h3 className="font-heading text-xl font-semibold">{detail.list.name}</h3>
              </div>
              <BtnSecondary onClick={() => setDetail(null)} testid="detail-close">Close</BtnSecondary>
            </div>
            <div className="border-t border-[#2A2A2A] overflow-x-auto">
              <table className="dtable w-full text-xs">
                <thead>
                  <tr>
                    <th>#</th>
                    {columns.map((c) => (
                      <th key={c} className="whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.recipients_sample.map((r) => (
                    <tr key={r.id}>
                      <td className="data-num text-[#71717A]">{r.original_row_number}</td>
                      {columns.map((c) => (
                        <td key={c} className={c === "email" ? "font-mono" : c === "cta_link" ? "font-mono text-[#00E5FF] truncate max-w-xs" : "truncate max-w-xs"}>
                          {String(rowValue(r, c) ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
